import React, { useState } from 'react';
import { bookingPaymentService, formatCurrency } from '../../services/bookingPaymentService';
import './VoucherPayPalButton.css'; // Reuse existing PayPal button styles

interface BookingPayPalButtonProps {
  bookingId: number;
  amount: number;
  onSuccess?: (result: { bookingId: number; amount: number }) => void;
  onError: (error: string) => void;
  onCancel?: () => void;
  disabled?: boolean;
}

/**
 * PayPal Button Component for Booking Payments
 *
 * This component handles the PayPal payment flow for bookings:
 * 1. Creates a PayPal order via our backend (/api/payment/initiate)
 * 2. Redirects to PayPal for approval
 * 3. (Return page handles capture)
 */
const BookingPayPalButton: React.FC<BookingPayPalButtonProps> = ({
  bookingId,
  amount,
  onSuccess,
  onError,
  onCancel,
  disabled = false,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const handlePayPalClick = async () => {
    if (disabled || isLoading) return;

    setIsLoading(true);

    try {
      const baseUrl = window.location.origin;
      const returnUrl = `${baseUrl}/buchung/success?bookingId=${bookingId}`;
      const cancelUrl = `${baseUrl}/booking?cancelled=true&bookingId=${bookingId}`;

      console.log('Initiating PayPal payment for booking:', bookingId);

      const result = await bookingPaymentService.initiatePayPalPayment(
        bookingId,
        returnUrl,
        cancelUrl
      );

      console.log('PayPal initiation result:', result);

      if (result.success && result.paymentUrl) {
        // Redirect to PayPal
        window.location.href = result.paymentUrl;
      } else {
        onError(result.errorMessage || 'Konnte PayPal-Zahlung nicht starten');
        setIsLoading(false);
      }
    } catch (err: any) {
      console.error('PayPal payment error:', err);
      onError(err.message || 'Fehler beim Starten der PayPal-Zahlung');
      setIsLoading(false);
    }
  };

  return (
    <div className="voucher-paypal-button-container">
      <button
        className="voucher-paypal-button"
        onClick={handlePayPalClick}
        disabled={isLoading || disabled}
      >
        {isLoading ? (
          <span className="paypal-loading">
            <span className="spinner"></span>
            Wird verarbeitet...
          </span>
        ) : (
          <>
            <span className="paypal-logo-text">
              <span className="pay">Pay</span>
              <span className="pal">Pal</span>
            </span>
            <span className="paypal-amount">{formatCurrency(amount)}</span>
          </>
        )}
      </button>
      <p className="paypal-secure-note">
        <span className="secure-icon">🔒</span>
        Sichere Zahlung über PayPal
      </p>
    </div>
  );
};

export default BookingPayPalButton;
